import apiClient from './apiClient';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_BASE_URL = `${BASE_URL}/api`;

export const plansApi = {
	// Get all plans for a team
	getTeamPlans: async (teamId, status = null) => {
		try {
			let url = `/api/plans/team/${teamId}`;

			// Add status parameter if provided
			const params = {};
			if (status) params.status = status;

			const response = await apiClient.get(url, { params });
			return response.data;
		} catch (error) {
			console.error('Get plans error:', error);
			throw new Error(
				error.response?.data?.message || 'Failed to fetch plans'
			);
		}
	},

	// Get all plans for an organization
	getAll: async (orgId, teamId = null) => {
		try {
			let url = `/api/plans/organization/${orgId}`;

			// Add query parameters if provided
			const params = {};
			if (teamId) params.teamId = teamId;

			const response = await apiClient.get(url, { params });
			return response.data;
		} catch (error) {
			console.error('Get plans error:', error);
			throw new Error(
				error.response?.data?.message || 'Failed to fetch plans'
			);
		}
	},

	// Get a specific plan
	getOne: async (planId) => {
		try {
			const response = await apiClient.get(`/api/plans/${planId}`);
			return response.data;
		} catch (error) {
			console.error('Get plan error:', error);
			throw new Error(
				error.response?.data?.message || 'Failed to fetch plan'
			);
		}
	},

	// Create a new plan
	create: async (planData) => {
		try {
			const response = await apiClient.post(`/api/plans`, planData);
			return response.data;
		} catch (error) {
			console.error('Create plan error:', error);
			throw new Error(
				error.response?.data?.message || 'Failed to create plan'
			);
		}
	},

	// Update a plan
	update: async (planId, planData) => {
		try {
			const response = await apiClient.patch(
				`/api/plans/${planId}`,
				planData
			);
			return response.data;
		} catch (error) {
			console.error('Update plan error:', error);
			throw new Error(
				error.response?.data?.message || 'Failed to update plan'
			);
		}
	},

	// Delete a plan
	delete: async (planId) => {
		try {
			const response = await apiClient.delete(`/api/plans/${planId}`);
			return response.data;
		} catch (error) {
			console.error('Delete plan error:', error);
			throw new Error(
				error.response?.data?.message || 'Failed to delete plan'
			);
		}
	},

	// Update plan status
	updateStatus: async (planId, status) => {
		try {
			const response = await apiClient.patch(
				`${API_BASE_URL}/plans/${planId}/status`,
				{ status },
				{
					withCredentials: true,
					headers: {
						'Content-Type': 'application/json',
					},
				}
			);
			return response.data;
		} catch (error) {
			console.error('Update plan status error:', error);
			throw new Error(
				error.response?.data?.message || 'Failed to update plan status'
			);
		}
	},

	// Delete a section
	deleteSection: async (sectionId) => {
		try {
			const response = await apiClient.delete(
				`${API_BASE_URL}/plans/sections/${sectionId}`,
				{
					withCredentials: true,
				}
			);
			return response.data;
		} catch (error) {
			console.error('Delete section error:', error);
			throw new Error(
				error.response?.data?.message || 'Failed to delete section'
			);
		}
	},

	// Delete a block
	deleteBlock: async (blockId) => {
		try {
			const response = await apiClient.delete(
				`${API_BASE_URL}/plans/blocks/${blockId}`,
				{
					withCredentials: true,
				}
			);
			return response.data;
		} catch (error) {
			console.error('Delete block error:', error);
			throw new Error(
				error.response?.data?.message || 'Failed to delete block'
			);
		}
	},

	// Reorder a section
	reorderSection: async (sectionId, newOrder) => {
		try {
			const response = await apiClient.patch(
				`${API_BASE_URL}/plans/sections/${sectionId}/reorder`,
				{ newOrder },
				{
					withCredentials: true,
					headers: {
						'Content-Type': 'application/json',
					},
				}
			);
			return response.data;
		} catch (error) {
			console.error('Reorder section error:', error);
			throw new Error(
				error.response?.data?.message || 'Failed to reorder section'
			);
		}
	},

	// Reorder a block (optionally move to different section)
	reorderBlock: async (blockId, newOrder, newSectionId = null) => {
		try {
			const response = await apiClient.patch(
				`${API_BASE_URL}/plans/blocks/${blockId}/reorder`,
				{ newOrder, newSectionId },
				{
					withCredentials: true,
					headers: {
						'Content-Type': 'application/json',
					},
				}
			);
			return response.data;
		} catch (error) {
			console.error('Reorder block error:', error);
			throw new Error(
				error.response?.data?.message || 'Failed to reorder block'
			);
		}
	},

	// Add team members to a plan
	addTeamMembers: async (planId, userIds) => {
		try {
			const response = await apiClient.post(
				`${API_BASE_URL}/plans/${planId}/team-members`,
				{ userIds },
				{
					withCredentials: true,
					headers: {
						'Content-Type': 'application/json',
					},
				}
			);
			return response.data;
		} catch (error) {
			console.error('Add team members error:', error);
			throw new Error(
				error.response?.data?.message || 'Failed to add team members'
			);
		}
	},

	// Add team members without refreshing the entire plan
	addTeamMembersNoRefresh: async (planId, userIds) => {
		try {
			// Make the API call to add team members
			await apiClient.post(
				`${API_BASE_URL}/plans/${planId}/team-members`,
				{ userIds },
				{
					withCredentials: true,
					headers: {
						'Content-Type': 'application/json',
					},
				}
			);

			// After successful addition, verify the updated team members by fetching the plan
			// but only return the teamMembers part to avoid a full plan refresh
			const verifyResponse = await apiClient.get(
				`${API_BASE_URL}/plans/${planId}`,
				{
					withCredentials: true,
					headers: {
						'Content-Type': 'application/json',
					},
				}
			);

			// Return the verified team members list
			return {
				success: true,
				teamMembers: verifyResponse.data.plan.teamMembers || [],
			};
		} catch (error) {
			console.error('Add team members error:', error);
			throw new Error(
				error.response?.data?.message || 'Failed to add team members'
			);
		}
	},

	// Remove team members from a plan
	removeTeamMembers: async (planId, userIds) => {
		try {
			const response = await apiClient.delete(
				`${API_BASE_URL}/plans/${planId}/team-members`,
				{
					withCredentials: true,
					headers: {
						'Content-Type': 'application/json',
					},
					data: { userIds }, // axios requires 'data' for delete with body
				}
			);
			return response.data;
		} catch (error) {
			console.error('Remove team members error:', error);
			throw new Error(
				error.response?.data?.message || 'Failed to remove team members'
			);
		}
	},

	// Update all team members for a plan (replace existing)
	updateTeamMembers: async (planId, userIds) => {
		try {
			const response = await apiClient.put(
				`${API_BASE_URL}/plans/${planId}/team-members`,
				{ userIds },
				{
					withCredentials: true,
					headers: {
						'Content-Type': 'application/json',
					},
				}
			);
			return response.data;
		} catch (error) {
			console.error('Update team members error:', error);
			throw new Error(
				error.response?.data?.message || 'Failed to update team members'
			);
		}
	},

	// Add media to a plan
	addPlanMedia: async (planId, mediaIds) => {
		try {
			const response = await apiClient.post(
				`${API_BASE_URL}/plans/${planId}/media`,
				{ mediaIds },
				{
					withCredentials: true,
					headers: {
						'Content-Type': 'application/json',
					},
				}
			);
			return response.data;
		} catch (error) {
			console.error('Add plan media error:', error);
			throw new Error(
				error.response?.data?.message || 'Failed to add media to plan'
			);
		}
	},

	// Remove media from a plan
	removePlanMedia: async (planId, mediaId) => {
		try {
			const response = await apiClient.delete(
				`${API_BASE_URL}/plans/${planId}/media/${mediaId}`,
				{
					withCredentials: true,
				}
			);
			return response.data;
		} catch (error) {
			console.error('Remove plan media error:', error);
			throw new Error(
				error.response?.data?.message ||
					'Failed to remove media from plan'
			);
		}
	},

	// Update all media for a plan (replace existing)
	updatePlanMedia: async (planId, mediaIds) => {
		try {
			const response = await apiClient.put(
				`${API_BASE_URL}/plans/${planId}/media`,
				{ mediaIds },
				{
					withCredentials: true,
					headers: {
						'Content-Type': 'application/json',
					},
				}
			);
			return response.data;
		} catch (error) {
			console.error('Update plan media error:', error);
			throw new Error(
				error.response?.data?.message || 'Failed to update plan media'
			);
		}
	},

	// Get team members for a plan
	getTeamMembers: async (planId) => {
		try {
			const response = await apiClient.get(
				`${API_BASE_URL}/plans/${planId}/team-members`,
				{
					withCredentials: true,
				}
			);
			return response.data;
		} catch (error) {
			console.error('Get team members error:', error);
			throw new Error(
				error.response?.data?.message || 'Failed to fetch team members'
			);
		}
	},

	// Associate media with a block
	addBlockMedia: async (blockId, mediaId) => {
		try {
			const response = await apiClient.put(
				`${API_BASE_URL}/plans/blocks/${blockId}/media`,
				{ mediaIds: [mediaId] }, // Send as array to match backend expectation
				{
					withCredentials: true,
					headers: {
						'Content-Type': 'application/json',
					},
				}
			);
			return response.data;
		} catch (error) {
			console.error('Add block media error:', error);
			throw new Error(
				error.response?.data?.message || 'Failed to add media to block'
			);
		}
	},

	// Update block's media
	updateBlockMedia: async (blockId, updates) => {
		try {
			const response = await apiClient.put(
				`${API_BASE_URL}/plans/blocks/${blockId}/media`,
				updates,
				{
					withCredentials: true,
					headers: {
						'Content-Type': 'application/json',
					},
				}
			);
			return response.data;
		} catch (error) {
			console.error('Update block media error:', error);
			throw new Error(
				error.response?.data?.message || 'Failed to update block media'
			);
		}
	},

	// Remove media from block
	removeBlockMedia: async (blockId) => {
		try {
			const response = await apiClient.delete(
				`${API_BASE_URL}/plans/blocks/${blockId}/media`,
				{
					withCredentials: true,
				}
			);
			return response.data;
		} catch (error) {
			console.error('Remove block media error:', error);
			throw new Error(
				error.response?.data?.message || 'Failed to remove block media'
			);
		}
	},

	// Assign role to a team member
	assignRole: async (planId, userId, { roleId, notes }) => {
		try {
			const response = await apiClient.post(
				`${API_BASE_URL}/plans/${planId}/members/${userId}/roles`,
				{ roleId, notes },
				{
					withCredentials: true,
					headers: {
						'Content-Type': 'application/json',
					},
				}
			);
			return response.data;
		} catch (error) {
			console.error('Assign role error:', error);
			throw new Error(
				error.response?.data?.message || 'Failed to assign role'
			);
		}
	},

	// Bulk update role assignments
	updateRoles: async (planId, assignments) => {
		try {
			// For bulk operations, we should use the POST endpoint
			const response = await apiClient.post(
				`${API_BASE_URL}/plans/${planId}/roles`,
				assignments, // Send the assignments array directly
				{
					withCredentials: true,
					headers: {
						'Content-Type': 'application/json',
					},
				}
			);
			return response.data;
		} catch (error) {
			console.error('Update roles error:', error);
			throw new Error(
				error.response?.data?.message || 'Failed to update roles'
			);
		}
	},

	// Update a specific role assignment
	updateRoleAssignment: async (planId, assignmentId, updates) => {
		try {
			const response = await apiClient.put(
				`${API_BASE_URL}/plans/${planId}/roles/${assignmentId}`,
				updates,
				{
					withCredentials: true,
					headers: {
						'Content-Type': 'application/json',
					},
				}
			);
			return response.data;
		} catch (error) {
			console.error('Update role assignment error:', error);
			throw new Error(
				error.response?.data?.message ||
					'Failed to update role assignment'
			);
		}
	},

	// Assign roles to multiple team members
	assignRoles: async (planId, data) => {
		try {
			const response = await apiClient.post(
				`${API_BASE_URL}/plans/${planId}/roles`,
				data,
				{
					withCredentials: true,
					headers: {
						'Content-Type': 'application/json',
					},
				}
			);
			return response.data;
		} catch (error) {
			console.error('Assign roles error:', error);
			throw new Error(
				error.response?.data?.message || 'Failed to assign roles'
			);
		}
	},

	getRoles: async (planId) => {
		try {
			const response = await apiClient.get(
				`${API_BASE_URL}/plans/${planId}/roles`,
				{
					withCredentials: true,
				}
			);
			return response.data;
		} catch (error) {
			console.error('Get roles error:', error);
			throw new Error(
				error.response?.data?.message || 'Failed to fetch roles'
			);
		}
	},

	getTeamRoles: async (teamId) => {
		try {
			const response = await apiClient.get(
				`${API_BASE_URL}/teams/${teamId}/roles`,
				{
					withCredentials: true,
				}
			);
			return response.data;
		} catch (error) {
			console.error('Get team roles error:', error);
			throw new Error(
				error.response?.data?.message || 'Failed to fetch team roles'
			);
		}
	},

	createBlock: async (sectionId, blockData) => {
		const response = await apiClient.post(
			`${API_BASE_URL}/plans/${sectionId}/blocks`,
			blockData,
			{
				withCredentials: true,
				headers: {
					'Content-Type': 'application/json',
				},
			}
		);
		return response.data;
	},

	updateSection: async (sectionId, updates) => {
		try {
			const response = await apiClient.put(
				`${API_BASE_URL}/plans/sections/${sectionId}`,
				updates,
				{
					withCredentials: true,
					headers: {
						'Content-Type': 'application/json',
					},
				}
			);
			return response.data;
		} catch (error) {
			console.error('Update section error:', error);
			throw error;
		}
	},

	updateBlock: async (blockId, updates) => {
		try {
			const response = await apiClient.put(
				`${API_BASE_URL}/plans/blocks/${blockId}`,
				updates,
				{
					withCredentials: true,
					headers: {
						'Content-Type': 'application/json',
					},
				}
			);
			return response.data;
		} catch (error) {
			console.error('Update block error:', error);
			throw error;
		}
	},

	createSection: async (planId, sectionData) => {
		try {
			const response = await apiClient.post(
				`${API_BASE_URL}/plans/${planId}/sections`,
				sectionData,
				{
					withCredentials: true,
					headers: {
						'Content-Type': 'application/json',
					},
				}
			);
			return response.data;
		} catch (error) {
			console.error('Create section error:', error);
			throw error;
		}
	},

	createBlock: async (sectionId, blockData) => {
		try {
			const response = await apiClient.post(
				`${API_BASE_URL}/plans/sections/${sectionId}/blocks`,
				blockData,
				{
					withCredentials: true,
					headers: {
						'Content-Type': 'application/json',
					},
				}
			);
			return response.data;
		} catch (error) {
			console.error('Create block error:', error);
			throw error;
		}
	},

	updateBlockMedia: async (blockId, { mediaIds }) => {
		try {
			const response = await apiClient.put(
				`${API_BASE_URL}/plans/blocks/${blockId}/media`,
				{ mediaIds },
				{
					withCredentials: true,
					headers: {
						'Content-Type': 'application/json',
					},
				}
			);
			return response.data;
		} catch (error) {
			console.error('Update block media error:', error);
			throw error;
		}
	},

	// Remove a single team member from a plan
	removeTeamMember: async (planId, userId) => {
		try {
			// Make the API call to remove the team member
			const response = await apiClient.delete(
				`${API_BASE_URL}/plans/${planId}/team-members`,
				{
					data: { userIds: [userId] },
					withCredentials: true,
					headers: {
						'Content-Type': 'application/json',
					},
				}
			);

			// After successful deletion, verify the user is gone by fetching the plan
			// but only return the teamMembers part to avoid a full plan refresh
			const verifyResponse = await apiClient.get(
				`${API_BASE_URL}/plans/${planId}`,
				{
					withCredentials: true,
					headers: {
						'Content-Type': 'application/json',
					},
				}
			);

			// Return the verified team members list
			return {
				success: true,
				teamMembers: verifyResponse.data.plan.teamMembers || [],
			};
		} catch (error) {
			console.error('Remove team member error:', error);
			throw new Error(
				error.response?.data?.message || 'Failed to remove team member'
			);
		}
	},
};

/*
EXAMPLE USAGE:

// /**
//  * GET /api/plans/organization/:orgId
//  * Get all plans for an organization
//  */
// // Request: Query parameters
// {
//     status: 'draft' // Optional: 'draft', 'published', 'archived'
// }

// // Response
// {
//     plans: [
//         {
//             id: 1,
//             mainTitle: "Sunday Morning Service",
//             description: "First service of the day",
//             organizationId: 1,
//             eventId: 123,
//             visibility: "private",
//             status: "draft",
//             estimatedDuration: 90,
//             sections: [
//                 {
//                     id: 1,
//                     title: "Opening",
//                     description: "Opening sequence",
//                     estimatedDuration: 15,
//                     order: 0,
//                     blocks: [
//                         {
//                             id: 1,
//                             type: "worship",
//                             title: "Opening Worship",
//                             content: "Amazing Grace",
//                             notes: "Start with first verse acapella",
//                             estimatedDuration: 5,
//                             order: 0,
//                             mediaId: 456,
//                             media: {
//                                 id: 456,
//                                 name: "Amazing Grace",
//                                 fileType: "pdf",
//                                 fileUrl: "https://example.com/song.pdf"
//                             }
//                         }
//                     ]
//                 }
//             ],
//             event: {
//                 id: 123,
//                 name: "Sunday Service",
//                 startDate: "2024-03-24T09:00:00Z"
//             }
//         }
//     ]
// }

// /**
//  * GET /api/plans/:planId
//  * Get a specific plan
//  */
// // Response
// {
//     plan: {
//         // Same structure as above plan object
//     }
// }

// /**
//  * POST /api/plans
//  * Create a new plan
//  */
// // Request
// {
//     mainTitle: "Sunday Evening Service",
//     description: "Evening worship service",
//     organizationId: 1,
//     eventId: 124,
//     visibility: "private",
//     status: "draft",
//     estimatedDuration: 75,
//     sections: [
//         {
//             title: "Pre-Service",
//             description: "Preparation and prayer",
//             estimatedDuration: 15,
//             blocks: [
//                 {
//                     type: "prayer",
//                     title: "Team Prayer",
//                     content: "Prayer points for the service",
//                     estimatedDuration: 10,
//                     order: 0
//                 }
//             ]
//         },
//         {
//             title: "Worship Set",
//             description: "Main worship sequence",
//             estimatedDuration: 30,
//             blocks: [
//                 {
//                     type: "worship",
//                     title: "Opening Song",
//                     content: "How Great Is Our God",
//                     notes: "Full band arrangement",
//                     estimatedDuration: 5,
//                     order: 0,
//                     mediaId: 457
//                 }
//             ]
//         }
//     ]
// }

// // Response
// {
//     plan: {
//         id: 2,
//         // ... created plan with all sections and blocks
//     }
// }

// /**
//  * PUT /api/plans/:planId
//  * Update a plan
//  */
// // Request
// {
//     mainTitle: "Updated Service Title",
//     description: "Updated description",
//     visibility: "public",
//     status: "published",
//     estimatedDuration: 80,
//     sections: [
//         // Updated sections array
//     ]
// }

// // Response
// {
//     plan: {
//         id: 2,
//         // ... updated plan with all sections and blocks
//     }
// }

// /**
//  * PATCH /api/plans/:planId/status
//  * Update plan status
//  */
// // Request
// {
//     status: "published" // or "draft" or "archived"
// }

// // Response
// {
//     plan: {
//         id: 2,
//         status: "published",
//         // ... rest of plan data
//     }
// }

// /**
//  * DELETE /api/plans/:planId
//  * Delete a plan
//  */
// // Response
// {
//     message: "Successfully deleted"
// }

// /**
//  * DELETE /api/plans/sections/:sectionId
//  * Delete a section
//  */
// // Response
// {
//     plan: {
//         // ... updated plan with section removed and others reordered
//     }
// }

// /**
//  * DELETE /api/plans/blocks/:blockId
//  * Delete a block
//  */
// // Response
// {
//     plan: {
//         // ... updated plan with block removed and others reordered
//     }
// }

// /**
//  * PATCH /api/plans/sections/:sectionId/reorder
//  * Reorder a section
//  */
// // Request
// {
//     newOrder: 2
// }

// // Response
// {
//     plan: {
//         // ... updated plan with reordered sections
//     }
// }

// /**
//  * PATCH /api/plans/blocks/:blockId/reorder
//  * Reorder a block (optionally to different section)
//  */
// // Request
// {
//     newOrder: 1,
//     newSectionId: 456  // Optional: Include to move to different section
// }

// // Response
// {
//     plan: {
//         // ... updated plan with reordered blocks
//     }
// }

// /**
//  * Error Response Format (for all endpoints)
//  */
// // 400 Bad Request / 403 Forbidden / 404 Not Found
// {
//     message: "Error message here",
//     errors: ["Detailed error message"]
// }

// // Get all plans for an organization
// const { plans } = await plansApi.getAll(orgId);

// // Get only published plans
// const { plans } = await plansApi.getAll(orgId, 'published');

// // Get a specific plan
// const { plan } = await plansApi.getOne(planId);

// // Create a new plan
// const newPlan = {
//     mainTitle: "Sunday Service",
//     organizationId: orgId,
//     // ... other fields
// };
// const { plan } = await plansApi.create(newPlan);

// // Update a plan
// const updates = {
//     mainTitle: "Updated Title",
//     sections: [/* ... */]
// };
// const { plan } = await plansApi.update(planId, updates);

// // Update plan status
// const { plan } = await plansApi.updateStatus(planId, 'published');

// // Delete a plan
// await plansApi.delete(planId);

// // Delete a section (automatically reorders remaining sections)
// const { plan } = await plansApi.deleteSection(sectionId);

// // Delete a block (automatically reorders remaining blocks)
// const { plan } = await plansApi.deleteBlock(blockId);

// // Reorder a section
// const { plan } = await plansApi.reorderSection(sectionId, 2);

// // Reorder a block within same section
// const { plan } = await plansApi.reorderBlock(blockId, 3);

// // Move a block to different section
// const { plan } = await plansApi.reorderBlock(blockId, 0, newSectionId);
